import React, { useCallback } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Dimensions,
} from 'react-native';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useHomeScreen } from '@/hooks/residents/useHomeScreen';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { homeStrings } from '@/constants/residents';

// Layout components
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { MobileScreenLayout } from '@/components/residents/mobile/MobileScreenLayout';

// Home screen card components
import {
  EventCard,
  WiFiCard,
  MarketplaceCard,
  ResourceCard,
  LinkedAccountsCard,
  SectionTitle,
  BulletinCard,
} from '@/components';
import { PaymentSummaryCard } from '@/components/residents/home/PaymentSummaryCard';
import { MaintenanceRequestCard } from '@/components/residents/home/MaintenanceRequestCard';

// Desktop view
import { DesktopHomeView } from './desktop';

const SCREEN_WIDTH = Dimensions.get('window').width;

/**
 * HomeScreen - Main home screen component with responsive layout
 * 
 * Renders mobile or desktop view based on screen size using useResponsive hook.
 * Following the established pattern from MaintenanceScreen.tsx and MessagesScreen.tsx.
 */
export const HomeScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';

  if (isDesktop) {
    return <DesktopHomeView />;
  }

  return <MobileHomeView />;
};

/**
 * MobileHomeView - Mobile home screen view
 * 
 * Displays the home dashboard with cards for payments, bulletins,
 * maintenance, events, community resources, and linked accounts.
 */
const MobileHomeView: React.FC = () => {
  const {
    resources,
    linkedAccounts,
    unreadBulletinsCount,
    validEvents,
    balanceDue,
    openTickets,
    isPaymentLoading,
    isMaintenanceLoading,
    refreshing,
    shouldShowPayments,
    shouldShowEventsSection,
    shouldShowWiFiCard,
    handleRefresh,
    handleBulletinPress,
    handleEventPress,
    handleWiFiPress,
    handleResourcePress,
    handleLinkedAccountsPress,
    handleMarketplacePress,
    handlePaymentsPress,
    handleMakePayment,
    handleMaintenancePress,
  } = useHomeScreen();

  // Render resource grid based on position like iOS
  const renderResourceGrid = useCallback(() => {
    const sortedResources = resources
      .filter(resource => resource.position !== undefined && resource.position >= 0 && resource.position <= 3);

    if (sortedResources.length === 1) {
      return (
        <ResourceCard
          resource={sortedResources[0]}
          isFullWidth
          onPress={handleResourcePress}
        />
      );
    }

    const firstRowResources = sortedResources.filter(r => r.position === 0 || r.position === 1);
    const secondRowResources = sortedResources.filter(r => r.position === 2 || r.position === 3);
    
    return (
      <View style={styles.resourcesGrid}>
        {firstRowResources.length > 0 && (
          <View style={styles.resourceRow}>
            {firstRowResources.map((resource) => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                onPress={handleResourcePress}
              />
            ))}
            {firstRowResources.length === 1 && <View style={styles.resourceSpacer} />}
          </View>
        )}
        
        {secondRowResources.length > 0 && (
          <View style={styles.resourceRow}>
            {secondRowResources.map((resource) => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                onPress={handleResourcePress}
              />
            ))}
            {secondRowResources.length === 1 && <View style={styles.resourceSpacer} />}
          </View>
        )}
      </View>
    );
  }, [resources, handleResourcePress]);

  return (
    <BaseResidentMobileScreen
      screenName="Home"
      darkBackground={true}
    >
      <MobileScreenLayout
        backgroundColor={MARKETPLACE_COLORS.background}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        refreshTintColor={MARKETPLACE_COLORS.textPrimary}
        lightStatusBar={true}
        contentGap={16}
      >
        {/* Payment Summary Card */}
        {shouldShowPayments && (
          <PaymentSummaryCard
            balanceDue={balanceDue}
            isLoading={isPaymentLoading}
            onPress={handlePaymentsPress}
            onMakePayment={handleMakePayment}
          />
        )}

        {/* Bulletin Card */}
        <BulletinCard
          unreadCount={unreadBulletinsCount}
          onPress={handleBulletinPress}
        />

        {/* Maintenance Request Card */}
        <MaintenanceRequestCard
          openTicketsCount={openTickets?.length || 0}
          isLoading={isMaintenanceLoading}
          onPress={handleMaintenancePress}
        />

        {/* Events Section */}
        {shouldShowEventsSection && (
          <View style={styles.eventsSection}>
            <SectionTitle title={homeStrings.upcomingEvents.title} />
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.eventsScrollContainer}
              style={styles.eventsCollectionView}
            >
              {validEvents.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onPress={handleEventPress}
                />
              ))}
            </ScrollView>
          </View>
        )}

        {/* Community Resources Section */}
        <View style={styles.communityResourcesSection}>
          <SectionTitle title={homeStrings.communityResources.title} />
          
          <MarketplaceCard onPress={handleMarketplacePress} />
          
          {shouldShowWiFiCard && <WiFiCard onPress={handleWiFiPress} />}

          {renderResourceGrid()}

          <ResourceCard
            isMoreResourcesCard
            isFullWidth
            onPress={handleResourcePress}
          />
        </View>

        {/* Linked Accounts Section */}
        <LinkedAccountsCard 
          linkedAccounts={linkedAccounts}
          onPress={handleLinkedAccountsPress}
        />
      </MobileScreenLayout>
    </BaseResidentMobileScreen>
  );
};

const styles = StyleSheet.create({
  eventsSection: {
    marginHorizontal: -20, // Break out of MobileScreenLayout padding
    paddingHorizontal: 20,
  },
  eventsCollectionView: {
    height: SCREEN_WIDTH / 2,
    marginHorizontal: -20,
  },
  eventsScrollContainer: {
    paddingHorizontal: 8,
    alignItems: 'flex-start',
  },
  communityResourcesSection: {
    gap: 16,
  },
  resourcesGrid: {
    gap: 16,
  },
  resourceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  resourceSpacer: {
    width: (SCREEN_WIDTH - 40 - 16) / 2, // Account for padding and gap
  },
});

// Keep backward compatibility with existing ResidentHomeScreen export
export { HomeScreen as ResidentHomeScreen };

export default HomeScreen;
